import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/shared/lib/utils";

import { getUtcOffsetLabel, resolveMetricRange } from "./range-utils";
import type { MetricRangePreset, MetricRangeValue } from "./types";

const PRESETS: { value: MetricRangePreset; label: string }[] = [
  { value: "today", label: "Hoy" },
  { value: "last24h", label: "Últimas 24h" },
  { value: "last7d", label: "7 días" },
  { value: "month", label: "Mes" },
  { value: "custom", label: "Custom" },
];

/** Interpreta Y/M/D del date picker como día civil **local** YYYY-MM-DD */
function calendarDayAsLocalDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export interface MetricRangeSelectorProps {
  value: MetricRangeValue;
  onChange: (value: MetricRangeValue) => void;
  className?: string;
}

/**
 * Selector de rango del hub de métricas (Hoy/mes = calendario local).
 */
export function MetricRangeSelector({
  value,
  onChange,
  className,
}: MetricRangeSelectorProps): React.JSX.Element {
  const [customDate, setCustomDate] = useState<Date | undefined>(() => {
    if (!value.date) return undefined;
    const [y, m, d] = value.date.split("-").map(Number);
    return new Date(y, m - 1, d);
  });

  const handlePreset = (preset: MetricRangePreset) => {
    if (preset === "custom") {
      const date = customDate
        ? calendarDayAsLocalDateKey(customDate)
        : calendarDayAsLocalDateKey(new Date());
      onChange(resolveMetricRange("custom", new Date(), { date }));
      return;
    }
    onChange(resolveMetricRange(preset));
  };

  const handleCustomDay = (date: Date | undefined) => {
    if (!date) return;
    setCustomDate(date);
    onChange(
      resolveMetricRange("custom", new Date(), { date: calendarDayAsLocalDateKey(date) })
    );
  };

  return (
    <div className={cn("flex flex-col gap-3 sm:flex-row sm:items-end", className)}>
      <div className="min-w-[160px] flex-1">
        <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
          Período
        </label>
        <Select
          value={value.preset}
          onValueChange={(v) => handlePreset(v as MetricRangePreset)}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Seleccionar período" />
          </SelectTrigger>
          <SelectContent>
            {PRESETS.map((p) => (
              <SelectItem key={p.value} value={p.value}>
                {p.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {value.preset === "custom" && (
        <div className="min-w-[180px] flex-1">
          <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Día (local)
          </label>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  "w-full justify-start text-left font-normal",
                  !customDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {customDate ? format(customDate, "PPP") : "Seleccionar día"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={customDate}
                onSelect={handleCustomDay}
                disabled={(date) => date > new Date() || date < new Date("2020-01-01")}
                captionLayout="dropdown"
              />
            </PopoverContent>
          </Popover>
        </div>
      )}

      <p className="whitespace-nowrap pb-2 text-xs text-muted-foreground">
        Zona: {getUtcOffsetLabel()} · períodos en hora local
      </p>
    </div>
  );
}
